// src/components/lesson/diagrams/Polygon.tsx
//
// Regular polygon (3–12 sides) for SAT "Lines, Angles, and Triangles" interior-
// angle-sum / regular-polygon questions. Vertices sit on a circle; the polygon
// is rotated so a side is flat at the bottom. Optionally labels one side, one
// interior angle, and the vertices.
//
// Params (all optional):
//   sides?: number          // 3–12, default 6
//   sideLabel?: string      // label the bottom side
//   angleLabel?: string     // label one interior angle (e.g. "x°")
//   vertexLabels?: string[]  // label vertices in order
//   label?: string

type PolygonParams = {
  sides?: number
  sideLabel?: string
  angleLabel?: string
  vertexLabels?: string[]
  label?: string
}

const SIZE = 280
const PAD = 40
const CX = SIZE / 2
const CY = SIZE / 2
const R = SIZE / 2 - PAD

const SHAPE = 'rgb(148 163 184)'
const FILL = 'rgb(34 211 238 / 0.10)'
const SIDE = 'rgb(34 211 238)'
const ANGLE = 'rgb(251 191 36)'

export default function Polygon({ params }: { params: Record<string, unknown> }) {
  const p = params as PolygonParams
  const nRaw = typeof p.sides === 'number' ? Math.round(p.sides) : 6
  const n = Math.max(3, Math.min(12, nRaw))

  // Rotate so a flat side sits at the bottom. Vertices CCW; SVG y is down so we
  // add sin (vertices go clockwise on screen, which is fine for a regular shape).
  const start = -90 + 180 / n
  const verts = Array.from({ length: n }, (_, i) => {
    const a = ((start + (360 / n) * i) * Math.PI) / 180
    return { x: CX + R * Math.cos(a), y: CY + R * Math.sin(a) }
  })

  const centroid = { x: CX, y: CY }
  const vLabels = Array.isArray(p.vertexLabels) ? p.vertexLabels : []

  // Bottom side = the edge whose midpoint has the largest screen-y.
  let bottomIdx = 0
  let bestY = -Infinity
  for (let i = 0; i < n; i++) {
    const my = (verts[i].y + verts[(i + 1) % n].y) / 2
    if (my > bestY) {
      bestY = my
      bottomIdx = i
    }
  }
  const bs = verts[bottomIdx]
  const be = verts[(bottomIdx + 1) % n]
  const bottomMid = { x: (bs.x + be.x) / 2, y: (bs.y + be.y) / 2 }

  // One interior-angle label: at vertex 0, nudged toward the centroid.
  const v0 = verts[0]
  const angLp = {
    x: v0.x + (centroid.x - v0.x) * 0.22,
    y: v0.y + (centroid.y - v0.y) * 0.22,
  }

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      role="img"
      aria-label={p.label ?? `Regular ${n}-gon`}
      className="my-3 w-full max-w-xs rounded-md bg-card/60 ring-1 ring-slate-800"
    >
      <polygon
        points={verts.map((v) => `${v.x},${v.y}`).join(' ')}
        fill={FILL}
        stroke={SHAPE}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />

      {p.sideLabel && (
        <text
          x={bottomMid.x}
          y={bottomMid.y + 16}
          fontSize="12"
          fontWeight="600"
          textAnchor="middle"
          fill={SIDE}
        >
          {p.sideLabel}
        </text>
      )}

      {p.angleLabel && (
        <text
          x={angLp.x}
          y={angLp.y}
          fontSize="12"
          textAnchor="middle"
          dominantBaseline="middle"
          fill={ANGLE}
        >
          {p.angleLabel}
        </text>
      )}

      {verts.map((v, i) => {
        const text = vLabels[i]
        if (!text) return null
        const lp = {
          x: v.x + (v.x - centroid.x) * 0.14,
          y: v.y + (v.y - centroid.y) * 0.14,
        }
        return (
          <text
            key={`v-${i}`}
            x={lp.x}
            y={lp.y}
            fontSize="11"
            fontWeight="700"
            textAnchor="middle"
            dominantBaseline="middle"
            fill={SHAPE}
          >
            {text}
          </text>
        )
      })}
    </svg>
  )
}
